import { SanityDiscussionSlide, SanityDiscussionStatConfig } from "@common";
import { DiscussionSlideData } from "@natklok/config/discussionSlideData";
import { localizedStringArrayToTranslationData } from "../translation";
import { blue, green, pink, purple, red, yellow } from "@mui/material/colors";
import { answerStatsTypes } from "@natklok/interfaces/answerStats.interface";

const colorMap = {
  red: {
    brightColor: red[200],
    mainColor: red[400],
    darkColor: red[500],
  },
  yellow: {
    brightColor: yellow[400],
    mainColor: yellow[800],
    darkColor: yellow[900],
  },
  green: {
    brightColor: green[200],
    mainColor: green[400],
    darkColor: green[500],
  },
  blue: {
    brightColor: blue[200],
    mainColor: blue[400],
    darkColor: blue[500],
  },
  purple: {
    brightColor: purple[200],
    mainColor: purple[400],
    darkColor: purple[500],
  },
  pink: {
    brightColor: pink[200],
    mainColor: pink[400],
    darkColor: pink[500],
  },
};

export function transformDiscussionSlide(slide: SanityDiscussionSlide): DiscussionSlideData {
  return {
    id: slide._id,
    title: localizedStringArrayToTranslationData(slide.title).sv,
    desc: localizedStringArrayToTranslationData(slide.description).sv,
    allowComments: slide.allowComments,
    statConfig: transformStatConfig(slide.statConfig),
  };
}

function transformStatConfig(
  statConfig: SanityDiscussionStatConfig
): DiscussionSlideData["statConfig"] {
  switch (statConfig.statType) {
    case "bubbles":
      return {
        type: answerStatsTypes.bubbles,
        title: localizedStringArrayToTranslationData(statConfig.bubblesStatConfig.title).sv,
        labelAndColorForVal1: {
          label: localizedStringArrayToTranslationData(
            statConfig.bubblesStatConfig.bubbles[0].label
          ).sv,
          ...colorMap[statConfig.bubblesStatConfig.bubbles[0].color],
        },
        labelAndColorForVal2: {
          label: localizedStringArrayToTranslationData(
            statConfig.bubblesStatConfig.bubbles[1].label
          ).sv,
          ...colorMap[statConfig.bubblesStatConfig.bubbles[1].color],
        },
        labelAndColorForVal3: {
          label: localizedStringArrayToTranslationData(
            statConfig.bubblesStatConfig.bubbles[2].label
          ).sv,
          ...colorMap[statConfig.bubblesStatConfig.bubbles[2].color],
        },
      };
      break;
    case "barChart": {
      const config: DiscussionSlideData["statConfig"] = {
        type: answerStatsTypes.barChart,
        title: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.title).sv,
        labelAndColorForBar1: {
          label: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.bars[0].label)
            .sv,
          ...colorMap[statConfig.barChartStatConfig.bars[0].color],
        },
        labelAndColorForBar2: {
          label: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.bars[1].label)
            .sv,
          ...colorMap[statConfig.barChartStatConfig.bars[1].color],
        },
        labelAndColorForBar3: {
          label: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.bars[2].label)
            .sv,
          ...colorMap[statConfig.barChartStatConfig.bars[2].color],
        },
      };
      if (statConfig.barChartStatConfig.bars[3]) {
        config.labelAndColorForBar4 = {
          label: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.bars[3].label)
            .sv,
          ...colorMap[statConfig.barChartStatConfig.bars[3].color],
        };
      }
      if (statConfig.barChartStatConfig.bars[4]) {
        config.labelAndColorForBar5 = {
          label: localizedStringArrayToTranslationData(statConfig.barChartStatConfig.bars[4].label)
            .sv,
          ...colorMap[statConfig.barChartStatConfig.bars[4].color],
        };
      }
      return config;
      break;
    }
    case "horizontalBar": {
      const config: DiscussionSlideData["statConfig"] = {
        type: answerStatsTypes.horizontalBar,
        title: localizedStringArrayToTranslationData(statConfig.barStatConfig.title).sv,
        labelAndColorForBar1: {
          label: localizedStringArrayToTranslationData(statConfig.barStatConfig.bars[0].label).sv,
          ...colorMap[statConfig.barStatConfig.bars[0].color],
        },
        labelAndColorForBar2: {
          label: localizedStringArrayToTranslationData(statConfig.barStatConfig.bars[1].label).sv,
          ...colorMap[statConfig.barStatConfig.bars[1].color],
        },
        labelAndColorForBar3: {
          label: localizedStringArrayToTranslationData(statConfig.barStatConfig.bars[2].label).sv,
          ...colorMap[statConfig.barStatConfig.bars[2].color],
        },
      };
      if (statConfig.barStatConfig.bars[3]) {
        config.labelAndColorForBar4 = {
          label: localizedStringArrayToTranslationData(statConfig.barStatConfig.bars[3].label).sv,
          ...colorMap[statConfig.barStatConfig.bars[3].color],
        };
      }
      if (statConfig.barStatConfig.bars[4]) {
        config.labelAndColorForBar5 = {
          label: localizedStringArrayToTranslationData(statConfig.barStatConfig.bars[4].label).sv,
          ...colorMap[statConfig.barStatConfig.bars[4].color],
        };
      }
      return config;
      break;
    }
    case "wordCloud":
      return {
        type: answerStatsTypes.wordCloud,
        textColor: statConfig.wordCloudStatConfig.textColor,
      };
    default:
      throw new Error(`Unknown stat config: ${statConfig}`);
  }
  return null as any;
}
